import {
	type ActorRefFrom,
	assign,
	fromPromise,
	sendTo,
	setup,
	type StateFrom,
} from 'xstate';

import { fetchEndpointData } from 'api';
import type { Cart, CartAddress, Customer } from 'models/api';

import type {
	UserInfoInput,
	UserInfoMachineContext,
	UserInfoMachineEvents,
} from './userInfo.types';

export const userInfoMachine = setup({
	// oxlint-disable-next-line typescript/no-unnecessary-type-assertion
	types: {} as {
		context: UserInfoMachineContext;
		events: UserInfoMachineEvents;
		input: UserInfoInput;
	},
	actions: {
		updateData: assign({
			email: (_, params: { cart: Cart | undefined }) => params.cart?.email,
			telephoneNumber: (_, params) => params.cart?.telephoneNumber,
			customerAddress: (_, params) => params.cart?.customerAddress,
			deliveryAddress: (_, params) => params.cart?.deliveryAddress,
			isAlternativeDeliveryAddressSelectable: (_, params) =>
				params.cart?.isAlternativeDeliveryAddressSelectable,
			alternativeDeliveryAddress: (_, params) =>
				params.cart?.alternativeDeliveryAddress,
			vatRegistrationNumber: (_, event) => event?.cart?.vatRegistrationNumber,
			freeReferenceText: (_, event) => event?.cart?.freeReferenceText,
			companyIdentificationNumber: (_, params) =>
				params.cart?.companyIdentificationNumber,
			error: (_, params) => params.cart?.errorList,
			allowAnonymousReviews: (_, params) =>
				params.cart?.allowAnonymousReviews ?? false,
		}),
		saveCustomerInfoToContext: assign({
			email: (
				{ context },
				params: {
					companyIdentificationNumber: string | undefined;
					customerAddress: CartAddress | undefined;
					email: string | undefined;
					freeReferenceText: string | undefined;
					telephoneNumber: string | undefined;
					vatRegistrationNumber: string | undefined;
				},
			) => params?.email ?? context.email,
			customerAddress: ({ context }, params) => ({
				...context.customerAddress,
				...params?.customerAddress,
			}),
			telephoneNumber: (_, params) => params.telephoneNumber,
			vatRegistrationNumber: (_, params) => params.vatRegistrationNumber,
			freeReferenceText: (_, params) => params.freeReferenceText,
			companyIdentificationNumber: (_, params) =>
				params.companyIdentificationNumber,
		}),
		saveAltAddressToContext: assign({
			deliveryAddress: (
				{ context },
				params: { deliveryAddress: CartAddress | undefined },
			) => ({
				...context.deliveryAddress,
				...params?.deliveryAddress,
			}),
		}),
		saveEmailAndPostalCodeToContext: assign({
			email: (
				_,
				params: { email: string | undefined; postalCode: string | undefined },
			) => params.email,
			customerAddress: ({ context }, params) => ({
				...context.customerAddress,
				postalCode: params.postalCode,
			}),
		}),
		savePostalCodeToContext: assign({
			customerAddress: (
				{ context },
				params: { postalCode: string | undefined },
			) => ({
				...context.customerAddress,
				postalCode: params.postalCode,
			}),
		}),
		setUseAlternativeAddress: assign({
			alternativeDeliveryAddress: (
				_,
				params: { useAlternativeDeliveryAddress: boolean },
			) => params.useAlternativeDeliveryAddress,
		}),
		updateCart: sendTo(
			({ context }) => context.checkoutMachineRef,
			({ context }) => ({
				type: 'UPDATE_CART',
				updatedCartProperties: {
					email: context.email,
					telephoneNumber: context.telephoneNumber,
					customerAddress: context.customerAddress,
					deliveryAddress: context.deliveryAddress,
					alternativeDeliveryAddress: context.alternativeDeliveryAddress,
					vatRegistrationNumber: context?.vatRegistrationNumber,
					freeReferenceText: context?.freeReferenceText,
					companyIdentificationNumber: context?.companyIdentificationNumber,
				},
			}),
		),
		saveUseAlternativeAddress: sendTo(
			({ context }) => context.checkoutMachineRef,
			(_, params: { useAlternativeDeliveryAddress: boolean }) => ({
				type: 'UPDATE_CART',
				updatedCartProperties: {
					alternativeDeliveryAddress: params.useAlternativeDeliveryAddress,
				},
			}),
		),
		saveEmailAndPostalCode: sendTo(
			({ context }) => context.checkoutMachineRef,
			({ context }) => ({
				type: 'UPDATE_CART',
				updatedCartProperties: {
					email: context.email,
					customerAddress: context.customerAddress,
				},
			}),
		),
		savePostalCode: sendTo(
			({ context }) => context.checkoutMachineRef,
			({ context }) => ({
				type: 'UPDATE_CART',
				updatedCartProperties: {
					customerAddress: context.customerAddress,
				},
			}),
		),
		saveAllowAnonymousReviews: sendTo(
			({ context }) => context.checkoutMachineRef,
			({ context }) => ({
				type: 'UPDATE_CART',
				updatedCartProperties: {
					allowAnonymousReviews: context.allowAnonymousReviews,
				},
			}),
		),
		setCustomerInfo: assign({
			customer: (_, params: { customer: Customer | undefined }) =>
				params.customer,
		}),
		resetCustomerInfo: assign({
			customer: () => undefined,
		}),
		saveToggleAllowAnonymousReviewsToContext: assign({
			allowAnonymousReviews: ({ context }) => !context.allowAnonymousReviews,
		}),
	},
	actors: {
		searchCustomer: fromPromise(
			async ({
				input: { email },
			}: {
				input: { email: string | undefined };
			}) => {
				if (!email) {
					throw new Error('No email provided');
				}
				return fetchEndpointData({
					name: 'GetCustomerSearch',
					queryParams: { email },
				});
			},
		),
	},
}).createMachine({
	id: 'userInfo',
	initial: 'idle',

	context: ({ input: { checkoutMachineRef } }) => ({
		allowAnonymousReviews: false,
		checkoutMachineRef,
	}),
	states: {
		idle: {
			id: 'idle',
			on: {
				CART_UPDATED: {
					target: 'idle',
					actions: {
						type: 'updateData',
						params: ({ event }) => ({ cart: event.value }),
					},
				},
				UPDATE_USER_INFO_USE_ALT_ADDRESS: {
					target: 'loading',
					actions: [
						{
							type: 'setUseAlternativeAddress',
							params: ({ event }) => ({
								useAlternativeDeliveryAddress:
									event.value.useAlternativeDeliveryAddress,
							}),
						},
						{
							type: 'saveUseAlternativeAddress',
							params: ({ event }) => ({
								useAlternativeDeliveryAddress:
									event.value.useAlternativeDeliveryAddress,
							}),
						},
					],
				},
				UPDATE_USER_INFO_FROM_STEP_TWO_SIGN_IN: {
					target: 'checkIsCustomerAndUpdateCart',
					actions: {
						type: 'saveEmailAndPostalCodeToContext',
						params: ({ event }) => event,
					},
				},
				STEP_TWO_COMPANY_POSTAL_CODE_UPDATE: {
					target: 'loading',
					actions: [
						{ type: 'savePostalCodeToContext', params: ({ event }) => event },
						'savePostalCode',
					],
				},
			},
		},
		checkIsCustomerAndUpdateCart: {
			entry: 'saveEmailAndPostalCode',
			initial: 'loading',
			states: {
				loading: {
					on: {
						CART_UPDATED: {
							target: 'checkIsCustomer',
							actions: {
								type: 'updateData',
								params: ({ event }) => ({ cart: event.value }),
							},
						},
					},
				},
				checkIsCustomer: {
					invoke: {
						id: 'searchCustomer',
						src: 'searchCustomer',
						input: ({ context }) => ({
							email: context.email,
						}),
						onDone: {
							target: '#idle',
							actions: {
								type: 'setCustomerInfo',
								params: ({ event }) => ({ customer: event.output }),
							},
						},
						onError: {
							target: '#idle',
						},
					},
				},
			},
		},
		debounceFullUserInfoSave: {
			after: {
				250: { target: 'loading', actions: ['updateCart'] },
			},
		},
		debounceAllowAnonymousReviews: {
			after: {
				150: {
					target: 'loading',
					actions: ['saveAllowAnonymousReviews'],
				},
			},
		},
		loading: {
			on: {
				CART_UPDATED: {
					target: 'idle',
					actions: {
						type: 'updateData',
						params: ({ event }) => ({ cart: event.value }),
					},
				},
			},
		},
	},
	on: {
		UPDATE_CUSTOMER_INFO: {
			target: '.debounceFullUserInfoSave',
			reenter: true,
			actions: [
				{
					type: 'saveCustomerInfoToContext',
					params: ({ event }) => event,
				},
			],
		},
		UPDATE_DELIVERY_ADDRESS: {
			target: '.debounceFullUserInfoSave',
			reenter: true,
			actions: [
				{
					type: 'saveAltAddressToContext',
					params: ({ event }) => event,
				},
			],
		},
		RESET_USER_INFO_FROM_STEP_TWO_SIGN_IN: {
			actions: ['resetCustomerInfo'],
		},
		TOGGLE_ALLOW_ANONYMOUS_REVIEWS: {
			actions: 'saveToggleAllowAnonymousReviewsToContext',
			reenter: true,
			target: '.debounceAllowAnonymousReviews',
		},
	},
});
export type UserInfoMachineState = StateFrom<typeof userInfoMachine>;
export type UserInfoMachineActor = ActorRefFrom<typeof userInfoMachine>;
